// Full-screen photo viewer: choose quality (Xem trước / HD / Ultra) and file
// format (JPEG / PNG / WebP) and download. Payments come later (server paywall flag).

import { ApiError, api, type Format, type PhotoRef } from '../api/client';
import { TIERS, type TierId } from '../booth/tiers';
import { Capacitor } from '@capacitor/core';
import { h, toast } from './dom';
import { saveImage } from './share';

export interface ViewerInput {
  title: string;
  /** locally composed preview (watermarked), e.g. right after shooting */
  previewUrl: string | null;
  /** photo stored on the server */
  photo: PhotoRef | null;
  filename: string;
  /** shown when there is no server copy */
  offlineNote?: string;
}

const FORMATS: { id: Format; label: string; hint: string }[] = [
  { id: 'JPEG', label: 'JPG', hint: 'nhẹ, dễ chia sẻ' },
  { id: 'PNG', label: 'PNG', hint: 'không nén mất chi tiết' },
  { id: 'WEBP', label: 'WebP', hint: 'nhẹ nhất' },
];

async function fetchAndSave(url: string, filename: string) {
  if (!Capacitor.isNativePlatform()) {
    // presigned URL with content-disposition: attachment → the browser downloads it
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.download = filename;
    document.body.append(a);
    a.click();
    a.remove();
    return;
  }
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Lỗi tải ảnh (${r.status})`);
  await saveImage(await r.blob(), filename);
}

export async function openViewer(v: ViewerInput) {
  let format: Format = 'JPEG';
  const img = h('img', { alt: v.title, src: v.previewUrl ?? '' });
  const list = h('div.tiers');
  const formats = h('div.chips.formats');
  const overlay = h('div.viewer', {},
    h('div.sheet', {},
      h('header.vhead', {}, h('h2', {}, v.title), h('button.icon', { title: 'Đóng', onclick: () => overlay.remove() }, '✕')),
      h('div.vimg', {}, img),
      formats,
      list));
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.append(overlay);

  const loadImg = async () => {
    if (v.photo && !v.previewUrl) {
      try { img.src = (await api.download(v.photo.id, 'preview')).url; } catch { /* keeps blank */ }
    }
  };

  const render = () => {
    formats.replaceChildren(...(v.photo ? FORMATS.map((f) =>
      h('button.chip', { class: f.id === format ? 'on' : '', title: f.hint, onclick: () => { format = f.id; render(); } }, f.label)) : []));
    list.replaceChildren(...TIERS.map((t) => {
      const size = v.photo?.width ? `${v.photo.width * t.scale}×${(v.photo.height ?? 0) * t.scale}px` : '';
      const btn = h('button', { class: t.id === 'preview' ? 'secondary' : 'primary' }, 'Tải về');
      if (!v.photo && t.id !== 'preview') {
        btn.textContent = 'Chưa có';
        btn.disabled = true;
      } else {
        btn.onclick = () => void act(btn, async () => {
          const ext = format === 'JPEG' ? 'jpg' : format.toLowerCase();
          const name = v.filename.replace(/(\.\w+)?$/, `-${t.id}.${v.photo ? ext : 'jpg'}`);
          if (v.photo) {
            const d = await api.download(v.photo.id, t.id as TierId, format, true);
            await fetchAndSave(d.url, name);
          } else if (v.previewUrl) {
            await saveImage(await (await fetch(v.previewUrl)).blob(), name);
          }
        });
      }
      return h('div.tier', {},
        h('div', {}, h('b', {}, t.name), h('small', {}, t.description, size ? ` · ${size}` : '')),
        btn);
    }));
    if (!v.photo) list.append(h('p.note', {}, v.offlineNote ?? 'Đang lưu bộ ảnh lên máy chủ… bản HD / Ultra sẽ có sau ít giây.'));
  };

  const act = async (btn: HTMLButtonElement, fn: () => Promise<void>) => {
    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Đang tạo…';
    try {
      await fn();
    } catch (e) {
      toast(e instanceof ApiError && e.status === 402 ? 'Chất lượng này cần mua (sắp có)' : (e as Error).message, 3500);
    } finally {
      btn.disabled = false;
      btn.textContent = label;
    }
  };

  void loadImg();
  render();
  return {
    el: overlay,
    update(p: Partial<ViewerInput>) {
      if (p.photo && v.photo?.id === p.photo.id) return;
      Object.assign(v, p);
      render();
    },
  };
}
